// src/components/ActivatedCardsList.jsx - FIXED with Supabase Auth
import React, { useState, useEffect } from 'react';
import { RefreshCw, Send, Copy, ExternalLink, CreditCard, Wallet } from 'lucide-react';
import { supabase } from '../supabaseClient'; // ✅ ADDED: Import Supabase
import SendFromCardModal from './SendFromCardModal';
import './ActivatedCardsList.css';

const ActivatedCardsList = ({ userId }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://globalpay-backend-v2.onrender.com/api';

  useEffect(() => {
    if (userId) {
      fetchCards();
      
      const interval = setInterval(() => {
        fetchCards(true);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [userId]);

  const fetchCards = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      // ✅ FIX: Get Supabase session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Not authenticated');
      }

      const token = session.access_token;

      const response = await fetch(`${API_BASE_URL}/card-wallet/user/${userId}/cards`, {
        headers: {
          'Authorization': `Bearer ${token}`, // ✅ Using Supabase token
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cards');
      }

      const data = await response.json();
      
      if (data.success) {
        setCards(data.cards || []);
      } else {
        throw new Error(data.error || 'Failed to load cards');
      }
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCards();
  };

  const handleSend = (card) => {
    setSelectedCard(card);
    setShowSendModal(true);
  };

  const handleCopyAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const handleSendComplete = () => {
    setShowSendModal(false);
    setSelectedCard(null);
    fetchCards();
  };

  const formatBTC = (amount) => {
    return parseFloat(amount || 0).toFixed(8);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'ACTIVATED': 'bg-green-500',
      'WAITING_FOR_RELAY': 'bg-yellow-500',
      'PENDING': 'bg-blue-500'
    };
    
    const statusLabels = {
      'ACTIVATED': 'Active',
      'WAITING_FOR_RELAY': 'Pending',
      'PENDING': 'Pending'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[status] || 'bg-gray-500'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  if (loading && cards.length === 0) {
    return (
      <div className="activated-cards-container loading">
        <div className="loading-spinner"></div>
        <p>Loading your cards...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activated-cards-container error">
        <p className="error-message">{error}</p>
        <button onClick={() => fetchCards()} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="activated-cards-container empty">
        <CreditCard size={48} className="empty-icon" />
        <h3>No Activated Cards</h3>
        <p>You haven't activated any crypto cards yet.</p>
        <p className="text-sm mt-2">Activate a card to start using your crypto wallet.</p>
      </div>
    );
  }

  const totalBalance = cards.reduce((sum, card) => sum + (card.balance || 0), 0);

  return (
    <div className="activated-cards-container">
      {/* Header with Total Balance */}
      <div className="cards-header">
        <div className="header-content">
          <div className="header-left">
            <Wallet size={24} />
            <div>
              <h2>Your Crypto Cards</h2>
              <p className="cards-count">{cards.length} card{cards.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="header-right">
            <div className="total-balance">
              <span className="balance-label">Total Balance</span>
              <span className="balance-amount">{formatBTC(totalBalance)} BTC</span>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`refresh-button ${refreshing ? 'spinning' : ''}`}
              title="Refresh balances"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="cards-grid">
        {cards.map((card) => (
          <div key={card.id} className="crypto-card">
            {/* Card Header */}
            <div className="card-header">
              <div className="card-info">
                <CreditCard size={20} />
                <span className="card-number">{card.maskedCardNumber}</span>
              </div>
              {getStatusBadge(card.status)}
            </div>

            {/* Card Balance */}
            <div className="card-balance">
              <span className="balance-label">Balance</span>
              <span className="balance-amount">{formatBTC(card.balance)} BTC</span>
              {card.unconfirmedBalance > 0 && (
                <span className="unconfirmed-balance">
                  +{formatBTC(card.unconfirmedBalance)} pending
                </span>
              )}
            </div>

            {/* Card Details */}
            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">Denomination</span>
                <span className="detail-value">${card.denomination}</span>
              </div>
              
              {card.address && (
                <div className="detail-row address-row">
                  <span className="detail-label">Address</span>
                  <div className="address-value">
                    <span className="address-text">
                      {card.address.substring(0, 8)}...{card.address.slice(-6)}
                    </span>
                    <button
                      onClick={() => handleCopyAddress(card.address)}
                      className="copy-button"
                      title="Copy address"
                    >
                      {copiedAddress === card.address ? (
                        <span className="copied-text">✓</span>
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                    <a
                      href={`https://blockchair.com/bitcoin/address/${card.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="explorer-link"
                      title="View on explorer"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Card Actions */}
            <div className="card-actions">
              <button
                onClick={() => handleSend(card)}
                disabled={!card.canSend || card.balance <= 0}
                className="send-button"
              >
                <Send size={16} />
                Send BTC
              </button>
              
              {!card.canSend && card.status === 'WAITING_FOR_RELAY' && (
                <p className="action-note">Waiting for activation to complete...</p>
              )}
              
              {card.balance <= 0 && card.status === 'ACTIVATED' && (
                <p className="action-note">No balance available</p>
              )}
            </div>

            {/* Activated Date */}
            {card.activatedAt && (
              <div className="card-footer">
                <span className="footer-text">
                  Activated {new Date(card.activatedAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Send Modal */}
      {showSendModal && selectedCard && (
        <SendFromCardModal
          card={selectedCard}
          onClose={() => {
            setShowSendModal(false);
            setSelectedCard(null);
          }}
          onSuccess={handleSendComplete}
        />
      )}

      {/* Auto-refresh indicator */}
      {refreshing && (
        <div className="refresh-indicator">
          <RefreshCw size={16} className="spin" />
          <span>Updating balances...</span>
        </div>
      )}
    </div>
  );
};

export default ActivatedCardsList;